import {
  collection,
  doc,
  writeBatch,
  serverTimestamp,
  increment,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { AGENT_LEVELS } from "../../../hooks/useAccountType";

/**
 * Find the nearest SMD by traversing up the hierarchy through recruiters
 * @param {string} recruiterId - The ID of the recruiter to start from
 * @returns {Promise<{smdId: string, smdName: string} | null>}
 */
async function findNearestSMD(recruiterId) {
  if (!recruiterId) return null;

  const visited = new Set(); // Prevent infinite loops
  let currentId = recruiterId;
  const maxDepth = 20; // Safety limit
  let depth = 0;

  while (currentId && depth < maxDepth) {
    if (visited.has(currentId)) {
      console.warn("Circular reference detected in recruiter hierarchy");
      break;
    }
    visited.add(currentId);

    try {
      // Get the user document
      const userRef = doc(db, "users", currentId);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        console.warn(`User ${currentId} not found in hierarchy`);
        break;
      }

      const userData = userSnap.data();
      
      // Check if this user is an SMD
      if (userData.level === AGENT_LEVELS.SMD) {
        return {
          smdId: currentId,
          smdName: userData.name || userData.email || currentId,
        };
      }

      // Move up to the next recruiter
      currentId = userData.recruiterId;
      depth++;
    } catch (error) {
      console.error(`Error fetching user ${currentId}:`, error);
      break;
    }
  }

  return null;
}

export async function addProspect(form) {
  // Validate required fields
  if (
    !form.firstName?.trim() ||
    !form.lastName?.trim()
  ) {
    throw new Error("First name and last name are required");
  }

  // Require at least one contact method
  const email = form.email?.trim();
  const phone = form.phone?.trim();
  if (!email && !phone) {
    throw new Error("At least one contact method (email or phone) is required");
  }

  // Create document reference with auto-generated ID
  const prospectRef = doc(collection(db, "prospects"));
  const id = prospectRef.id;

  const name = `${form.firstName} ${form.lastName}`.trim();
  const now = serverTimestamp();

  const prospectData = {
    mark: form.mark || "default",
    id,
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    name,
    email: form.email.trim(),
    phone: form.phone?.trim() || "",
    leader: form.leader?.trim() || "",
    level: form.level?.trim() || "",
    recruiter: form.recruiter?.trim() || "",
    recruiterId: form.recruiterId?.trim() || "",
    top25: form.top25 || false,
    hot: form.hot || false,
    outcome: form.outcome?.trim() || "",
    managerEmail: form.managerEmail || null,
    code: form.code?.trim() || "",
    date: form.date || null,
    profile: form.profile || {},
    status: "prospect",
    notes: form.notes || "",
    files: form.files || [],
    createdAt: now,
    updatedAt: now,
  };
console.log('prospectData', prospectData);

  await setDoc(prospectRef, prospectData);

  return { id };
}

export async function inviteProspect(prospectId, leaderId, recruiterUserId = null) {
  // Get the prospect document
  const prospectRef = doc(db, "prospects", prospectId);
  const prospectSnap = await getDoc(prospectRef);

  if (!prospectSnap.exists()) {
    throw new Error(`Prospect with ID ${prospectId} not found`);
  }

  const prospect = prospectSnap.data();

  // Check if already invited - DISABLED FOR TESTING
  // if (prospect.status === "invited") {
  //   throw new Error("Prospect has already been invited");
  // }

  const now = serverTimestamp();
  const batch = writeBatch(db);

  // Find nearest SMD by traversing up the hierarchy
  const smdInfo = await findNearestSMD(prospect.recruiterId);

  // Build user document
  const userDoc = {
    email: prospect.email,
    leader: prospect.leader,
    leaderId: leaderId || prospect.leaderId || "",
    level: prospect.level,
    managerEmail: prospect.managerEmail || "",
    name: prospect.name,
    phone: prospect.phone,
    recruiter: prospect.recruiter || "",
    recruiterId: prospect.recruiterId || "",
    smdId: smdInfo?.smdId || "",
    smdName: smdInfo?.smdName || "",
    status: "Active",
    id: prospectId,
    invitedFrom: "prospects",
    invitedAt: now,
    createdAt: now,
    updatedAt: now,
  };

  // Build tracker document with default values
  const trackerDoc = {
    // Root identity
    id: prospectId,
    firstName: prospect.firstName,
    lastName: prospect.lastName,
    name: prospect.name,
    email: prospect.email,
    phone: prospect.phone,
    leader: prospect.leader,
    level: prospect.level,
    code: prospect.code || "",
    date: prospect.date || null,
    status: "Active",
    createdAt: now,
    updatedAt: now,

    // FourByFour section
    fourByFour: {
      application: false,
      eventReg: false,
      finish1st: false,
      passExam: false,
      practiceExam: false,
      recruit2: false,
      recruit3: false,
      recruit4: false,
      recruiter: prospect.recruiter || "",
      recruiterId: prospect.recruiterId || "",

      // split savings into four independent toggles + amounts
      savings1: false,
      savings1Amount: null,
      savings2: false,
      savings2Amount: null,
      savings3: false,
      savings3Amount: null,
      savings4: false,
      savings4Amount: null,

      licClass: "",
      email: prospect.email,
      phone: prospect.phone,
      leader: prospect.leader,
      level: prospect.level,
      code: prospect.code || "",
      date: prospect.date || null,
      createdAt: now,
      updatedAt: now,
    },

    // Associate section (FIXED)
    associate: {
      bigEventReg: false,
      finishFirstRecruit: false,
      getLicense: false, // existing boolean
      mVideosWatched: false,
      multiHanded: false,
      licensesInTtl: 0,

      // ✅ Use a single boolean for each milestone
      milestones: {
        observe4Recruits: false,
        observe4Clients: false,
        // keep in sync with 4x4/licensing if you mirror getLicensed here too
        getLicensed: false,
      },

      // ⛳ Legacy note: we are NOT creating obsCl1..obsCl8 anymore.
      // If you have old docs with those fields, they will be ignored by new UI.

      personalPoints: 0,
      personalSavings: 0,
      phone: prospect.phone,
      recruitTtl: 0,
      registrationConvention: false,
      selfImprovement: false,
      tenThreResultsGoals: false,
      email: prospect.email,
      leader: prospect.leader,
      level: prospect.level,
      code: prospect.code || "",
      date: prospect.date || null,
      createdAt: now,
      updatedAt: now,
    },

    // Licensing section
    licensing: {
      applicationSubmitted: false,
      attendedClass: false,
      completedCourse: false,
      fingerprint: false,
      licenseActive: false,
      passedExam: false,
      practiceExam: false,
      scheduledClass: false,
      scheduledExam: false,
      startedCourse: false,
      xcel: false,
      recruiter: prospect.recruiter || "",
      recruiterId: prospect.recruiterId || "",
      email: prospect.email,
      leader: prospect.leader,
      date: prospect.date || null,
      createdAt: now,
      updatedAt: now,
      notes: {},
    },
  };

  // Add operations to batch
  batch.set(doc(db, "users", prospectId), userDoc, { merge: true });
  batch.set(doc(db, "trackers", prospectId), trackerDoc, { merge: true });

  // Increment recruiter recruitTtl (associate tracker) when inviting a prospect
  if (prospect.recruiterId) {
    const recruiterTrackerRef = doc(db, "trackers", prospect.recruiterId);
    batch.set(
      recruiterTrackerRef,
      {
        associate: {
          recruitTtl: increment(1),
        },
        updatedAt: now,
      },
      { merge: true }
    );
  }

  // Update prospect with status and recruiterId (for associate tracker drill-down)
  const prospectUpdate = {
    status: "invited",
    userId: prospectId,
    updatedAt: now,
  };
  
  // Set recruiterId if provided (ID of the user who invited them)
  if (recruiterUserId) {
    prospectUpdate.recruiterId = recruiterUserId;
  }
  
  batch.update(prospectRef, prospectUpdate);

  // Execute atomic batch
  await batch.commit();
}

/**
 * Update SMD information for a user by finding their nearest SMD
 * @param {string} userId - The user ID to update
 * @returns {Promise<{smdId: string, smdName: string} | null>}
 */
export async function updateUserSMD(userId) {
  try {
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      throw new Error(`User ${userId} not found`);
    }

    const userData = userSnap.data();
    
    // If user is already an SMD, set themselves
    if (userData.level === "SMD") {
      await updateDoc(userRef, {
        smdId: userId,
        smdName: userData.name || userData.email || userId,
        updatedAt: serverTimestamp(),
      });
      return {
        smdId: userId,
        smdName: userData.name || userData.email || userId,
      };
    }

    // Find SMD by traversing up hierarchy
    const smdInfo = await findNearestSMD(userData.recruiterId);

    if (smdInfo) {
      await updateDoc(userRef, {
        smdId: smdInfo.smdId,
        smdName: smdInfo.smdName,
        updatedAt: serverTimestamp(),
      });
    }

    return smdInfo;
  } catch (error) {
    console.error(`Error updating SMD for user ${userId}:`, error);
    throw error;
  }
}

/**
 * Bulk update SMD information for all users
 * @returns {Promise<{success: number, failed: number}>}
 */
export async function updateAllUsersSMD() {
  try {
    const usersRef = collection(db, "users");
    const snapshot = await getDocs(usersRef);
    
    let success = 0;
    let failed = 0;

    for (const userDoc of snapshot.docs) {
      try {
        await updateUserSMD(userDoc.id);
        success++;
      } catch (error) {
        console.error(`Failed to update SMD for user ${userDoc.id}:`, error);
        failed++;
      }
    }

    console.log(`✅ SMD update complete: ${success} success, ${failed} failed`);
    return { success, failed };
  } catch (error) {
    console.error("Error in bulk SMD update:", error);
    throw error;
  }
}
